import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineDate,
  InlineSelect,
  InlineStatus,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────

type ContractorEngagement = {
  id: number
  person: number
  person_name: string
  subsidiary: number | null
  subsidiary_name: string | null
  subsidiary_code: string | null
  country_code: string
  governing_country: string
  engagement_number: string
  start_date: string
  end_date: string | null
  status: 'ACTIVE' | 'PAUSED' | 'ENDED'
  end_reason: string
  contractor_type: 'DIRECT' | 'AGENCY'
  employer_partner: number | null
  employer_partner_name: string | null
  freelancer_partner: number | null
  freelancer_partner_name: string | null
  active_contract: number | null
  active_contract_number: string | null
  job_title: string
  department: number | null
  department_name: string | null
  manager: number | null
  manager_name: string | null
  position: number | null
  position_code: string | null
  position_title: string | null
  notes: string
  is_currently_active: boolean
}

type PersonLite = { id: number; full_name: string }
type Department = { id: number; code: string; name: string; is_active: boolean }
type Subsidiary = { id: number; code: string; name: string; country_code: string }
type PartnerLite = {
  id: number
  name: string
  partner_type: 'CUSTOMER' | 'VENDOR' | 'BOTH' | 'FREELANCER'
}
type VendorContractLite = {
  id: number
  contract_number: string
  partner: number | null
}
type WorkerRecord = {
  id: number
  person: number
  record_type: 'EMPLOYMENT' | 'CONTRACTOR'
  contractor_engagement: number | null
}
type ProjectAssignment = {
  id: number
  project: number
  project_code: string
  project_name: string
  person_name: string | null
  worker_display_number: string | null
  worker_record_type: string | null
  role: 'CONSULTANT' | 'CONTRACTOR' | 'MANAGER'
  start_date: string
  end_date: string | null
  status: 'ACTIVE' | 'PAUSED' | 'ENDED'
}

const STATUS_COLOR: Record<ContractorEngagement['status'], string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  PAUSED: 'bg-amber-100 text-amber-800',
  ENDED: 'bg-slate-100 text-slate-700',
}

const ASSIGNMENT_STATUS_COLOR: Record<ProjectAssignment['status'], string> = {
  ACTIVE: 'bg-blue-100 text-blue-700',
  PAUSED: 'bg-amber-100 text-amber-700',
  ENDED: 'bg-slate-100 text-slate-700',
}

const END_REASON_LABEL: Record<string, string> = {
  CONTRACT_END: 'Contract end',
  TERMINATED: 'Terminated',
  OTHER: 'Other',
}

type TabKey = 'details' | 'commercial' | 'projects' | 'documents'

// ─────────────────── Page ───────────────────

export default function HRContractorEngagementDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const engagementId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const engagementQ = useQuery({
    queryKey: ['hr', 'contractor-engagement', engagementId],
    queryFn: async () =>
      (await api.get<ContractorEngagement>(`/hr/contractor-engagements/${engagementId}/`)).data,
    enabled: !!engagementId,
  })

  const personsQ = useQuery({
    queryKey: ['hr', 'persons', 'manager-candidates'],
    queryFn: async () =>
      (await api.get<PersonLite[]>('/hr/persons/?manager_candidates=1')).data,
  })
  const departmentsQ = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => (await api.get<Department[]>('/hr/departments/')).data,
  })
  const subsidiariesQ = useQuery({
    queryKey: ['subsidiaries'],
    queryFn: async () => (await api.get<Subsidiary[]>('/subsidiaries/')).data,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<PartnerLite[]>('/partners/')).data,
  })
  const vendorContractsQ = useQuery({
    queryKey: ['finance', 'vendor-contracts'],
    queryFn: async () => (await api.get<VendorContractLite[]>('/partners/vendor-contracts/')).data,
  })

  // Look up the WorkerRecord for this engagement so we can query assignments
  const workerRecordQ = useQuery({
    queryKey: ['hr', 'worker-records', { person: engagementQ.data?.person }],
    queryFn: async () =>
      (await api.get<WorkerRecord[]>(`/hr/worker-records/?person=${engagementQ.data!.person}`)).data,
    enabled: !!engagementQ.data?.person,
    select: (records) =>
      records.find((r) => r.record_type === 'CONTRACTOR' && r.contractor_engagement === engagementId),
  })

  const workerRecordId = workerRecordQ.data?.id

  const assignmentsQ = useQuery({
    queryKey: ['consulting', 'assignments', { worker_record: workerRecordId }],
    queryFn: async () =>
      (await api.get<ProjectAssignment[]>(`/consulting/assignments/?worker_record=${workerRecordId}`)).data,
    enabled: !!workerRecordId,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<ContractorEngagement>) =>
      (await api.patch(`/hr/contractor-engagements/${engagementId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'contractor-engagement', engagementId] })
      qc.invalidateQueries({ queryKey: ['hr', 'contractor-engagements'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/hr/contractor-engagements/${engagementId}/`),
    onSuccess: () => {
      toast.success('Contractor engagement deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'contractor-engagements'] })
      if (engagementQ.data?.person) {
        navigate(`/admin/hr/people/${engagementQ.data.person}`)
      } else {
        navigate('/admin/hr/people')
      }
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (engagementQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading...</p></AdminLayout>
  }
  if (engagementQ.isError || !engagementQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Contractor engagement not found.</p>
          <Link to="/admin/hr/people" className="text-blue-600 hover:underline">Back to people</Link>
        </div>
      </AdminLayout>
    )
  }

  const engagement = engagementQ.data
  const persons = personsQ.data || []
  const departments = (departmentsQ.data || []).filter((d) => d.is_active)
  const subsidiaries = subsidiariesQ.data || []
  const allPartners = partnersQ.data || []
  const agencyPartners = allPartners.filter(
    (p) => p.partner_type === 'VENDOR' || p.partner_type === 'BOTH'
  )
  const freelancerPartners = allPartners.filter((p) => p.partner_type === 'FREELANCER')
  const vendorContracts = vendorContractsQ.data || []
  const assignments = assignmentsQ.data || []

  const save = (payload: Partial<ContractorEngagement>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Page header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link
            to={`/admin/hr/people/${engagement.person}`}
            className="text-muted-foreground hover:text-foreground hover:underline"
          >
            {engagement.person_name}
          </Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{engagement.engagement_number}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={engagement.job_title} placeholder="Add job title" onSave={(v) => save({ job_title: v })} />
            </h2>
            <div className="text-sm text-muted-foreground">
              {engagement.contractor_type === 'DIRECT' ? 'Direct contractor' : 'Agency contractor'}
              {engagement.subsidiary_name
                ? <> &middot; {engagement.subsidiary_code} &mdash; {engagement.subsidiary_name}</>
                : <> &middot; Group entity</>}
              {engagement.governing_country && <> &middot; {engagement.governing_country}</>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={engagement.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'PAUSED', label: 'Paused' },
                { value: 'ENDED', label: 'Ended' },
              ]}
              onSave={(v) => save({ status: v as ContractorEngagement['status'] })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(`engagement ${engagement.engagement_number}`, () => deleteMut.mutate())}
                  className="text-destructive"
                >Delete engagement</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab nav */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'commercial', 'projects', 'documents'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t
                  ? 'border-blue-600 text-blue-700 font-semibold'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'commercial' && 'Commercial'}
              {t === 'projects' && `Projects (${assignments.length})`}
              {t === 'documents' && 'Documents'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab
          engagement={engagement}
          persons={persons}
          departments={departments}
          subsidiaries={subsidiaries}
          onSave={save}
        />
      )}
      {tab === 'commercial' && (
        <CommercialTab
          engagement={engagement}
          agencyPartners={agencyPartners}
          freelancerPartners={freelancerPartners}
          vendorContracts={vendorContracts}
          onSave={save}
        />
      )}
      {tab === 'projects' && (
        <ProjectsTab rows={assignments} loading={assignmentsQ.isLoading} />
      )}
      {tab === 'documents' && (
        <FilesTab parent="hr.contractorengagement" parentId={engagementId} />
      )}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  engagement, persons, departments, subsidiaries, onSave,
}: {
  engagement: ContractorEngagement
  persons: PersonLite[]
  departments: Department[]
  subsidiaries: Subsidiary[]
  onSave: (payload: Partial<ContractorEngagement>) => Promise<void>
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Structure">
        <dl className="grid grid-cols-[170px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Person">
            <Link to={`/admin/hr/people/${engagement.person}`} className="text-blue-600 hover:underline">
              {engagement.person_name}
            </Link>
          </Row>
          <Row label="Hiring entity">
            {engagement.subsidiary_name ? (
              <InlineSelect
                value={engagement.subsidiary ? String(engagement.subsidiary) : ''}
                displayLabel={
                  engagement.subsidiary_name
                    ? `${engagement.subsidiary_code} - ${engagement.subsidiary_name}`
                    : <Dash />
                }
                allowClear
                options={subsidiaries.map((s) => ({
                  value: String(s.id), label: `${s.code} - ${s.name}`, searchText: `${s.code} ${s.name}`,
                }))}
                onSave={(v) => onSave({ subsidiary: v ? Number(v) : null })}
              />
            ) : (
              <span className="text-muted-foreground text-sm">Group entity</span>
            )}
          </Row>
          <Row label="Governing country">
            <span className="font-mono text-xs uppercase">{engagement.governing_country || <Dash />}</span>
          </Row>
        </dl>
      </Card>

      <Card title="Lifecycle">
        <dl className="grid grid-cols-[170px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Start date">
            <InlineDate value={engagement.start_date} onSave={(v) => onSave({ start_date: v || undefined })} />
          </Row>
          <Row label="End date">
            <InlineDate value={engagement.end_date} onSave={(v) => onSave({ end_date: v })} />
          </Row>
          <Row label="End reason">
            <InlineSelect
              value={engagement.end_reason || ''}
              displayLabel={engagement.end_reason ? END_REASON_LABEL[engagement.end_reason] : <Dash />}
              allowClear
              options={Object.entries(END_REASON_LABEL).map(([k, v]) => ({ value: k, label: v }))}
              onSave={(v) => onSave({ end_reason: v })}
              helper="Required when end date is set."
            />
          </Row>
        </dl>
      </Card>

      <Card title="Role" className="lg:col-span-2">
        <dl className="grid grid-cols-[170px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Contractor type">
            <InlineSelect
              value={engagement.contractor_type}
              options={[
                { value: 'DIRECT', label: 'Direct (independent / freelancer)' },
                { value: 'AGENCY', label: 'Agency / staff-aug' },
              ]}
              onSave={(v) => onSave({ contractor_type: v as ContractorEngagement['contractor_type'] })}
              helper="Direct = freelancer/sole-trader; Agency = sent by staffing vendor."
            />
          </Row>
          <Row label="Job title">
            <InlineText
              value={engagement.job_title}
              onSave={(v) => onSave({ job_title: v })}
              placeholder="Add job title"
            />
          </Row>
          <Row label="Department">
            <InlineSelect
              value={engagement.department ? String(engagement.department) : ''}
              displayLabel={engagement.department_name || <Dash />}
              allowClear
              options={departments.map((d) => ({
                value: String(d.id), label: `${d.code} ${d.name}`, searchText: `${d.code} ${d.name}`,
              }))}
              onSave={(v) => onSave({ department: v ? Number(v) : null })}
            />
          </Row>
          <Row label="Manager">
            <InlineSelect
              value={engagement.manager ? String(engagement.manager) : ''}
              displayLabel={engagement.manager_name || <Dash />}
              allowClear
              options={persons
                .filter((p) => p.id !== engagement.person)
                .map((p) => ({ value: String(p.id), label: p.full_name, searchText: p.full_name }))}
              onSave={(v) => onSave({ manager: v ? Number(v) : null })}
              helper="Internal person who manages / oversees this engagement."
            />
          </Row>
          <Row label="Notes">
            <InlineText
              value={engagement.notes}
              multiline
              onSave={(v) => onSave({ notes: v })}
              placeholder="Add notes about this engagement"
            />
          </Row>
        </dl>
      </Card>
    </div>
  )
}

// ─────────────────── Commercial tab ───────────────────

function CommercialTab({
  engagement, agencyPartners, freelancerPartners, vendorContracts, onSave,
}: {
  engagement: ContractorEngagement
  agencyPartners: PartnerLite[]
  freelancerPartners: PartnerLite[]
  vendorContracts: VendorContractLite[]
  onSave: (payload: Partial<ContractorEngagement>) => Promise<void>
}) {
  const filteredContracts = engagement.employer_partner || engagement.freelancer_partner
    ? vendorContracts.filter((vc) =>
        vc.partner === engagement.employer_partner || vc.partner === engagement.freelancer_partner
      )
    : vendorContracts

  return (
    <div className="bg-card rounded-lg shadow p-6 max-w-3xl">
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        <Row label="Contractor type">
          <InlineSelect
            value={engagement.contractor_type}
            options={[
              { value: 'DIRECT', label: 'Direct (independent / freelancer)' },
              { value: 'AGENCY', label: 'Agency / staff-aug' },
            ]}
            onSave={(v) => onSave({ contractor_type: v as ContractorEngagement['contractor_type'] })}
          />
        </Row>
        {engagement.contractor_type === 'AGENCY' && (
          <Row label="Agency partner">
            <InlineSelect
              value={engagement.employer_partner ? String(engagement.employer_partner) : ''}
              displayLabel={engagement.employer_partner_name || <Dash />}
              allowClear
              options={agencyPartners.map((p) => ({ value: String(p.id), label: p.name, searchText: p.name }))}
              onSave={(v) => onSave({
                employer_partner: v ? Number(v) : null,
                active_contract: null,
              })}
              helper="Vendor/staffing company that sends this contractor."
            />
          </Row>
        )}
        {engagement.contractor_type === 'DIRECT' && (
          <Row label="Freelancer partner">
            <InlineSelect
              value={engagement.freelancer_partner ? String(engagement.freelancer_partner) : ''}
              displayLabel={engagement.freelancer_partner_name || <Dash />}
              allowClear
              options={freelancerPartners.map((p) => ({ value: String(p.id), label: p.name, searchText: p.name }))}
              onSave={(v) => onSave({
                freelancer_partner: v ? Number(v) : null,
                active_contract: null,
              })}
              helper="The freelancer's own Partner record."
            />
          </Row>
        )}
        <Row label="Active contract">
          <InlineSelect
            value={engagement.active_contract ? String(engagement.active_contract) : ''}
            displayLabel={engagement.active_contract_number || <Dash />}
            allowClear
            options={filteredContracts.map((vc) => ({
              value: String(vc.id), label: vc.contract_number, searchText: vc.contract_number,
            }))}
            onSave={(v) => onSave({ active_contract: v ? Number(v) : null })}
            helper="Current service / ICA / SOW contract."
          />
        </Row>
      </dl>
    </div>
  )
}

// ─────────────────── Projects tab ───────────────────

function ProjectsTab({ rows, loading }: { rows: ProjectAssignment[]; loading: boolean }) {
  if (loading) return <p className="text-muted-foreground">Loading...</p>
  if (rows.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
        No project assignments for this engagement.
      </div>
    )
  }
  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted/40">
          <tr>
            <Th>Project</Th>
            <Th>Role</Th>
            <Th>Start</Th>
            <Th>End</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((a) => (
            <tr key={a.id} className="hover:bg-muted/40">
              <Td className="text-sm">
                <Link to={`/admin/consulting/projects/${a.project}`} className="text-blue-600 hover:underline">
                  {a.project_code} - {a.project_name}
                </Link>
              </Td>
              <Td className="text-xs">{a.role}</Td>
              <Td className="text-xs">{a.start_date}</Td>
              <Td className="text-xs">{a.end_date || <Dash />}</Td>
              <Td>
                <span className={`text-xs px-2 py-0.5 rounded font-semibold ${ASSIGNMENT_STATUS_COLOR[a.status]}`}>
                  {a.status}
                </span>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────────── Helpers ───────────────────

function Card({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-card rounded-lg shadow p-6 ${className}`}>
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{title}</h3>
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>
      {children}
    </th>
  )
}

function Td({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`}>{children}</td>
}
